import { Box, Flex, Text } from '@chakra-ui/react'
import { forwardRef, useEffect, useMemo, useState } from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'
import type { ToastLike } from '@mangovoice/mango-ui'

import { type ReportingApi, ReportingApiProvider } from '../api'
import NumberComparisonChart from '../components/NumberComparisonChart'
import NumberComparisonTable from '../components/NumberComparisonTable'
import { ReportingDashboardShell } from '../components/ReportingDashboardShell'
import type { ViewType } from '../components/ViewToggle'
import { useDateContext, useIsDateRangeTooLarge } from '../hooks/useDateContext'
import { type DashboardExportHandle, useExportCsv } from '../hooks/useExportCsv'
import { useGetNumberComparison } from '../hooks/useGetNumberComparison'
import {
  ReportingI18nProvider,
  type ReportingTranslationLike,
  useReportingI18n,
} from '../i18n'
import type { DateRange, NumberData, ReportingPhoneNumber } from '../types'
import { formatPhoneNumber } from '../utils/formatter'

interface NumberComparisonDashboardInnerProps {
  sharedDateRange: DateRange
  selectedPbxId: number
  timeZone?: string
  selectedPhoneNumbers: ReportingPhoneNumber[]
  phoneNumbers: ReportingPhoneNumber[]
  phoneNumberMap: Map<string, ReportingPhoneNumber>
  isPhoneNumbersLoading: boolean
  onExportAvailabilityChange?: (canExport: boolean) => void
  toast?: ToastLike
  formatPhoneNumberFn?: (phone: string | undefined | null) => string
}

export type NumberComparisonDashboardHandle = DashboardExportHandle

const NumberComparisonDashboardInner = forwardRef<
  NumberComparisonDashboardHandle,
  NumberComparisonDashboardInnerProps
>(
  (
    {
      sharedDateRange,
      selectedPbxId,
      timeZone,
      selectedPhoneNumbers,
      phoneNumbers: _phoneNumbers,
      phoneNumberMap,
      isPhoneNumbersLoading: _isPhoneNumbersLoading,
      onExportAvailabilityChange,
      toast,
      formatPhoneNumberFn,
    },
    ref,
  ) => {
    const { text } = useReportingI18n()
    const [viewType, setViewType] = useState<ViewType>('chart')
    const [displayLimit, setDisplayLimit] = useState(50)
    const fmt = formatPhoneNumberFn ?? formatPhoneNumber

    useEffect(() => {
      setDisplayLimit(50)
    }, [viewType])

    useEffect(() => {
      setDisplayLimit(50)
    }, [sharedDateRange])

    const dateContext = useDateContext(sharedDateRange, timeZone)
    const isDateRangeTooLarge = useIsDateRangeTooLarge(sharedDateRange)

    const numberComparisonRequest = useMemo(() => {
      if (!dateContext) return undefined

      return {
        start: dateContext.startDateUtc,
        end: dateContext.endDateUtc,
        pbx: selectedPbxId.toString(),
        ...(selectedPhoneNumbers.length > 0 && {
          phone_number: selectedPhoneNumbers.map((p) => p.number),
        }),
        group_by: 'phone_number' as const,
      }
    }, [dateContext, selectedPbxId, selectedPhoneNumbers])

    const {
      data: numberComparisonData,
      isFetching: isLoading,
      error: apiError,
    } = useGetNumberComparison({
      request: numberComparisonRequest,
      enabled: !!dateContext && !isDateRangeTooLarge,
    })

    const hasError = !!apiError

    const allNumberData = useMemo<NumberData[]>(() => {
      const segments = numberComparisonData?.segments || []

      const segmentData = segments.map((segment) => {
        const phoneNumber =
          segment.phoneNumber ||
          text('numberComparisonDashboard.unknown', 'Unknown')
        const phoneFromMap = phoneNumberMap.get(phoneNumber)

        const displayNumber = fmt(phoneNumber) || phoneNumber

        const name = phoneFromMap?.name || displayNumber

        const calls = segment.counts?.calls ?? 0
        const totalSeconds = segment.counts?.seconds ?? 0
        const averageDuration = calls > 0 ? Math.round(totalSeconds / calls) : 0

        return {
          number: phoneNumber,
          displayNumber,
          name,
          inboundCalls: calls,
          totalSeconds,
          averageDuration,
        }
      })

      if (selectedPhoneNumbers.length > 0) {
        const segmentNumbersSet = new Set(
          segments
            .map((s) => s.phoneNumber)
            .filter((num): num is string => !!num && num.length > 0),
        )

        const missingNumbers = selectedPhoneNumbers.filter((phoneNum) => {
          if (!phoneNum?.number || phoneNum.number.length === 0) {
            return false
          }
          return !segmentNumbersSet.has(phoneNum.number)
        })

        const missingNumberData = missingNumbers.map((phoneNum) => {
          const phoneNumber = phoneNum.number
          const displayNumber = fmt(phoneNumber) || phoneNumber
          const name = phoneNum.name || displayNumber

          return {
            number: phoneNumber,
            displayNumber,
            name,
            inboundCalls: 0,
            totalSeconds: 0,
            averageDuration: 0,
          }
        })

        const combinedData = [...segmentData, ...missingNumberData]
        return combinedData.sort((a, b) => b.inboundCalls - a.inboundCalls)
      }

      return segmentData.sort((a, b) => b.inboundCalls - a.inboundCalls)
    }, [numberComparisonData, phoneNumberMap, selectedPhoneNumbers, text, fmt])

    const numberData = useMemo(
      () => allNumberData.slice(0, displayLimit),
      [allNumberData, displayLimit],
    )

    const hasMoreData = displayLimit < allNumberData.length
    const remainingCount = allNumberData.length - displayLimit

    const totals = useMemo(() => {
      if (allNumberData.length === 0) {
        return {
          totalCalls: 0,
          totalSeconds: 0,
          numberCount: 0,
          averageCalls: 0,
          averageDuration: 0,
          topNumber: text('numberComparisonDashboard.notAvailable', 'N/A'),
          topNumberCalls: 0,
        }
      }

      const totalCalls = allNumberData.reduce(
        (sum, item) => sum + (item.inboundCalls ?? 0),
        0,
      )
      const totalSeconds = allNumberData.reduce(
        (sum, item) => sum + (item.totalSeconds ?? 0),
        0,
      )
      const numberCount = allNumberData.length
      const averageCalls =
        numberCount > 0 ? Math.round(totalCalls / numberCount) : 0
      const averageDuration =
        totalCalls > 0 ? Math.round(totalSeconds / totalCalls) : 0
      const topNumber = allNumberData[0]

      return {
        totalCalls,
        totalSeconds,
        numberCount,
        averageCalls,
        averageDuration,
        topNumber:
          topNumber?.name ||
          text('numberComparisonDashboard.notAvailable', 'N/A'),
        topNumberCalls: topNumber?.inboundCalls || 0,
      }
    }, [allNumberData, text])

    const csvRows = useMemo(() => {
      const h = {
        phoneNumber: text(
          'dashboards.csvExport.headers.numberComparison.phoneNumber',
          'Phone Number',
        ),
        displayNumber: text(
          'dashboards.csvExport.headers.numberComparison.displayNumber',
          'Display Number',
        ),
        name: text(
          'dashboards.csvExport.headers.numberComparison.name',
          'Name',
        ),
        inboundCalls: text(
          'dashboards.csvExport.headers.numberComparison.inboundCalls',
          'Inbound Calls',
        ),
        totalDurationSeconds: text(
          'dashboards.csvExport.headers.numberComparison.totalDurationSeconds',
          'Total Duration (seconds)',
        ),
        averageDurationSeconds: text(
          'dashboards.csvExport.headers.numberComparison.averageDurationSeconds',
          'Average Duration (seconds)',
        ),
      }

      return allNumberData.map((row) => ({
        [h.phoneNumber]: row.number,
        [h.displayNumber]: row.displayNumber,
        [h.name]: row.name,
        [h.inboundCalls]: row.inboundCalls,
        [h.totalDurationSeconds]: row.totalSeconds,
        [h.averageDurationSeconds]: row.averageDuration,
      }))
    }, [allNumberData, text])

    useExportCsv({
      ref,
      csvRows,
      filenamePrefix: 'number_comparison',
      isLoading,
      hasError,
      toast,
      onExportAvailabilityChange,
    })

    const counters = useMemo(
      () => [
        {
          value: totals.totalCalls,
          label: text(
            'numberComparisonDashboard.counters.totalCalls.title',
            'Total Inbound Calls',
          ),
          subtitle: text(
            'numberComparisonDashboard.counters.totalCalls.subtitle',
            'Total inbound calls across all numbers',
          ),
        },
        {
          value: totals.numberCount,
          label: text(
            'numberComparisonDashboard.counters.numberCount.title',
            'Total Numbers',
          ),
          subtitle: text(
            'numberComparisonDashboard.counters.numberCount.subtitle',
            'Total phone numbers tracked',
          ),
        },
        {
          value: totals.averageCalls,
          label: text(
            'numberComparisonDashboard.counters.averageCalls.title',
            'Average per Number',
          ),
          subtitle: text(
            'numberComparisonDashboard.counters.averageCalls.subtitle',
            'Average inbound calls per number',
          ),
        },
        {
          value: totals.topNumberCalls,
          label: text(
            'numberComparisonDashboard.counters.topNumber.title',
            'Top Performer',
          ),
          subtitle: totals.topNumber,
        },
      ],
      [totals, text],
    )

    return (
      <ReportingDashboardShell
        timeZone={timeZone}
        dateContextReady={!!dateContext}
        isDateRangeTooLarge={isDateRangeTooLarge}
        isLoading={isLoading}
        hasError={hasError}
        hasData={numberData.length > 0}
        viewType={viewType}
        onViewTypeChange={setViewType}
        counters={counters}
        viewToggleMarginBottom="2.5rem"
        i18nKeys={{
          invalidDateRange: 'numberComparisonDashboard.invalidDateRange',
          gettingPbx: 'dashboards.gettingPbx',
          dateRangeTooLarge: 'numberComparisonDashboard.dateRangeTooLarge',
          dateRangeAdjustPrompt:
            'numberComparisonDashboard.dateRangeAdjustPrompt',
          errorText: 'numberComparisonDashboard.dashboard.errorText',
          noDataText: 'numberComparisonDashboard.dashboard.noDataText',
        }}
        chartHeaderContent={
          <Flex alignItems="center" justifyContent="space-between" mb="1rem">
            <Text {...textStyles[textStyleKeys.Body2_Bold]}>
              {text(
                'numberComparisonDashboard.chartTitle',
                'Call Volume by Number',
              )}
            </Text>
            {viewType === 'chart' && (
              <Text
                fontFamily="Inter"
                fontSize="12px"
                fontWeight="500"
                color="fg.muted"
                mr="60px"
              >
                {text(
                  'numberComparisonDashboard.showingCount',
                  'Showing {{displayed}} of {{total}}',
                  {
                    displayed: numberData.length,
                    total: allNumberData.length,
                  },
                )}
              </Text>
            )}
          </Flex>
        }
        chartContent={<NumberComparisonChart data={numberData} />}
        tableContent={<NumberComparisonTable data={numberData} />}
        afterContent={
          hasMoreData ? (
            <Flex justifyContent="center" mt="1.5rem">
              <Flex
                as="button"
                align="center"
                gap="0.5rem"
                px="1rem"
                py="0.5rem"
                bg="transparent"
                color="fg.muted"
                fontFamily="Inter"
                fontSize="13px"
                fontWeight="400"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  color: 'fg.default',
                }}
                onClick={() => setDisplayLimit((prev) => prev + 50)}
              >
                <Text>
                  {text(
                    'numberComparisonDashboard.showMore',
                    'Show More ({{remaining}} remaining)',
                    {
                      remaining: remainingCount,
                    },
                  )}
                </Text>
                <Box
                  w="14px"
                  h="14px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  ▾
                </Box>
              </Flex>
            </Flex>
          ) : undefined
        }
      />
    )
  },
)

export interface NumberComparisonDashboardProps extends NumberComparisonDashboardInnerProps {
  api: ReportingApi
  t?: ReportingTranslationLike
  locale?: string
}

export const NumberComparisonDashboard = forwardRef<
  NumberComparisonDashboardHandle,
  NumberComparisonDashboardProps
>(({ api, t, locale, ...rest }, ref) => {
  return (
    <ReportingApiProvider api={api}>
      <ReportingI18nProvider t={t} locale={locale}>
        <NumberComparisonDashboardInner ref={ref} {...rest} />
      </ReportingI18nProvider>
    </ReportingApiProvider>
  )
})

export default NumberComparisonDashboard
